import {RefObject, useRef, useState} from 'react';

import {isNil} from 'ramda';

import {useOnMount} from 'shared/web';

/**
 * Tells whether a line-clamped element hides part of its text. It re-measures whenever the element
 * resizes, because the number of lines follows the width of the preview.
 *
 * One observer lives as long as the component. The element must be rendered on mount; a component
 * whose element appears later remounts through a `key` instead.
 *
 * @param elementRef A ref attached to the clamped element
 */
export function useIsClamped(elementRef: RefObject<HTMLElement | null>) {
  const [isClamped, setIsClamped] = useState(false);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  useOnMount(
    function observeClamping() {
      const element = elementRef.current;

      if (isNil(element)) {
        return;
      }

      const measure = () => setIsClamped(element.scrollHeight > element.clientHeight);

      resizeObserverRef.current = new ResizeObserver(measure);
      resizeObserverRef.current.observe(element);

      measure();
    },
    function stopObservingClamping() {
      resizeObserverRef.current?.disconnect();
    }
  );

  return isClamped;
}
